/** Admin-only bulk intake queue: multi-file upload (PDF auto-split by
 *  keyword "RAČUN") + interactive approval queue with counter. */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Check, FileUp, Loader2, X } from "lucide-react";

const INPUT = "bg-black/40 border border-white/10 px-2 py-1.5 rounded-lg text-xs focus:outline-none focus:border-[#C5A059]/60 w-full";

function PageThumb({ itemId, page, onClick }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    api.get(`/admin/bulk-receipts/items/${itemId}/page/${page}?thumb=1`, { responseType: "blob" })
      .then((r) => { if (!revoked) setUrl(URL.createObjectURL(r.data)); })
      .catch(() => {});
    return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, page]);
  if (!url) return <div className="w-full h-40 bg-black/40 rounded-lg animate-pulse" />;
  const openFull = () => onClick();
  return (
    <img src={url} alt={`Stranica ${page + 1}`} onClick={openFull}
         className="w-full h-40 object-cover object-top rounded-lg border border-white/10 cursor-zoom-in"
         data-testid={`bulk-item-thumb-${itemId}`} />
  );
}

function QueueItem({ item, drivers, onDone }) {
  const [form, setForm] = useState({
    amount_eur: item.amount_eur ?? "",
    receipt_date: item.receipt_date || "",
    accounting_name: "",
    driver_id: "",
    category: "dijelovi",
  });
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reading = item.ocr_status === "processing";

  const openFull = async (n) => {
    try {
      const r = await api.get(`/admin/bulk-receipts/items/${item.id}/page/${n}`, { responseType: "blob" });
      setZoom(URL.createObjectURL(r.data));
    } catch { /* noop */ }
  };

  const approve = async () => {
    if (!form.amount_eur || Number(form.amount_eur) <= 0) return toast.error("Unesite iznos.");
    setBusy(true);
    try {
      await api.post(`/admin/bulk-receipts/items/${item.id}/approve`, {
        amount_eur: Number(form.amount_eur),
        receipt_date: form.receipt_date || null,
        accounting_name: form.accounting_name,
        driver_id: form.driver_id || null,
        category: form.category,
      });
      toast.success(`Račun ${item.invoice_number || ""} odobren.`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Odobravanje nije uspjelo.");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bulk-receipts/items/${item.id}/reject`);
      toast.success("Stavka odbijena.");
      onDone();
    } catch { toast.error("Greška."); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3 flex flex-col gap-2"
         data-testid={`bulk-queue-item-${item.id}`}>
      <PageThumb itemId={item.id} page={0} onClick={() => openFull(0)} />
      <div className="flex items-center justify-between text-[11px] text-zinc-400">
        <span className="truncate">{item.invoice_number ? `RAČUN ${item.invoice_number}` : item.source_file}</span>
        <span className="shrink-0 ml-2 px-1.5 py-0.5 rounded bg-white/5">{item.page_count} str.</span>
      </div>
      {item.page_count > 1 && (
        <div className="flex items-center gap-1 text-[10px] text-zinc-500">
          Stranice:
          {Array.from({ length: item.page_count }, (_, n) => (
            <button key={n} onClick={() => openFull(n)}
                    className="px-1.5 py-0.5 rounded border border-white/10 hover:border-[#C5A059]/50 hover:text-[#C5A059] transition"
                    data-testid={`bulk-page-btn-${item.id}-${n}`}>
              {n + 1}
            </button>
          ))}
        </div>
      )}
      {reading && (
        <div className="inline-flex items-center gap-1.5 text-[10px] text-[#C5A059]" data-testid={`bulk-ocr-reading-${item.id}`}>
          <Loader2 className="w-3 h-3 animate-spin" /> Čitam podatke s računa…
        </div>
      )}
      {!reading && item.ocr && (item.ocr.vat_amount_eur || item.ocr.issuer_oib) && (
        <div className="text-[10px] text-zinc-500 leading-relaxed" data-testid={`bulk-ocr-summary-${item.id}`}>
          {item.ocr.vat_amount_eur ? <>PDV: €{item.ocr.vat_amount_eur} · </> : null}
          {item.ocr.issuer_oib ? <>OIB izd.: {item.ocr.issuer_oib}</> : null}
        </div>
      )}
      <div className="grid grid-cols-2 gap-1.5">
        <input type="number" step="0.01" value={form.amount_eur} onChange={set("amount_eur")}
               placeholder="Iznos €" className={INPUT} data-testid={`bulk-amount-${item.id}`} />
        <input type="date" value={form.receipt_date} onChange={set("receipt_date")}
               className={`${INPUT} [color-scheme:dark]`} data-testid={`bulk-date-${item.id}`} />
        <input list="acct-driver-names" value={form.accounting_name} onChange={set("accounting_name")}
               placeholder="Ime za knjigovodstvo" className={`${INPUT} col-span-2`}
               data-testid={`bulk-acct-${item.id}`} />
        <select value={form.driver_id} onChange={set("driver_id")}
                className={INPUT} data-testid={`bulk-driver-${item.id}`}>
          <option value="">Bez vozača (trošak tvrtke)</option>
          {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={form.category} onChange={set("category")}
                className={INPUT} data-testid={`bulk-category-${item.id}`}>
          <option value="dijelovi">Dijelovi za vozilo</option>
          <option value="gorivo">Gorivo</option>
          <option value="servis">Servis vozila</option>
          <option value="gume">Gume</option>
          <option value="parking">Parking i cestarine</option>
          <option value="pranje">Pranje i održavanje</option>
        </select>
      </div>
      <div className="flex gap-1.5 mt-1">
        <button onClick={approve} disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-emerald-600/90 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50 transition"
                data-testid={`bulk-approve-${item.id}`}>
          <Check className="w-3.5 h-3.5" /> Odobri
        </button>
        <button onClick={reject} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-red-500/40 text-red-400 text-xs hover:bg-red-500/10 disabled:opacity-50 transition"
                data-testid={`bulk-reject-${item.id}`}>
          <X className="w-3.5 h-3.5" /> Odbij
        </button>
      </div>
      {zoom && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-start justify-center overflow-auto p-6"
             onClick={() => setZoom(null)} data-testid="bulk-zoom-overlay">
          <img src={zoom} alt="Pregled" className="max-w-3xl w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}

export function BulkQueuePanel({ drivers = [] }) {
  const [queue, setQueue] = useState({ pending: [], pending_count: 0, processing_jobs: [] });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const processing = queue.processing_jobs.length > 0
    || queue.pending.some((i) => i.ocr_status === "processing");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/bulk-receipts/queue");
      setQueue(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [processing, load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post("/admin/bulk-receipts/upload", fd);
      toast.success(data.jobs.length
        ? `${data.jobs.length} PDF u obradi — razdvajam po "RAČUN"…`
        : `${data.direct_items} slika dodano u red.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload nije uspio.");
    } finally { setUploading(false); }
  };

  if (!queue.pending_count && !processing) {
    return (
      <div className="mb-5 flex items-center gap-3">
        <input ref={fileRef} type="file" multiple accept="application/pdf,image/*"
               className="hidden" onChange={upload} data-testid="bulk-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] text-sm hover:bg-[#C5A059]/10 disabled:opacity-50 transition"
                data-testid="bulk-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
          Skupni unos (PDF / slike)
        </button>
        <span className="text-[11px] text-zinc-500">Samo admin — veliki PDF se automatski dijeli po ključnoj riječi "RAČUN".</span>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-2xl border border-[#C5A059]/25 bg-[#C5A059]/[0.04] p-4"
         data-testid="bulk-queue-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h3 className="text-sm font-semibold text-[#C5A059]">Red čekanja — skupni unos</h3>
        <span className="px-2 py-0.5 rounded-full bg-[#C5A059] text-black text-xs font-bold"
              data-testid="bulk-queue-counter">
          {queue.pending_count} na čekanju
        </span>
        {processing && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400" data-testid="bulk-processing">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#C5A059]" />
            {queue.processing_jobs.map((j) =>
              `${j.filename} — ${j.fallback === "ai" ? "AI razdvajanje" : "stranica"} ${j.pages_done || 0}/${j.total_pages || "?"} · ${j.items_created || 0} računa`
            ).join(" · ")}
          </span>
        )}
        <div className="ml-auto">
          <input ref={fileRef} type="file" multiple accept="application/pdf,image/*"
                 className="hidden" onChange={upload} data-testid="bulk-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] text-xs hover:bg-[#C5A059]/10 disabled:opacity-50 transition"
                  data-testid="bulk-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
            Dodaj još
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {queue.pending.map((item) => (
          <QueueItem key={`${item.id}-${item.ocr_status || "done"}`} item={item} drivers={drivers} onDone={load} />
        ))}
      </div>
    </div>
  );
}
